import React from 'react';
import { CheckIcon, CopyIcon, ChevronDownIcon, ExternalLinkIcon, SlidersHorizontalIcon } from 'lucide-react';
import { aiTargets } from '../../data/samplePrompts';
import { splitPrompt } from '../../utils/prompt';
import { usePromptCard } from './usePromptCard';
type Props = {
  n: number;
  title: string;
  text: string;
};
export function PromptCard({
  n,
  title,
  text
}: Props) {
  const p = usePromptCard(text);
  const segments = splitPrompt(text);
  return <article className="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_20px_50px_-30px_rgba(11,31,51,0.45)]" data-id="element-223" data-mp="133ijxc9uapad">
      <header className="flex items-center justify-between gap-3 bg-navy px-5 py-3" data-id="element-224" data-mp="u56vngwg43l3">
        <div className="min-w-0" data-id="element-225" data-mp="y5z62dh20eiy">
          <p className="text-[11px] font-bold uppercase tracking-wider text-cyan" data-id="element-226" data-mp="y7a8o9yrjg8h">Prompt #{n}</p>
          <h3 className="truncate font-display text-base font-black text-white" data-id="element-227" data-mp="k7iplute39nm">{title}</h3>
        </div>
        <button type="button" onClick={p.copy} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-xs font-bold text-white transition-colors duration-150 ease-out hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan" data-id="element-228" data-mp="p492yadt7r5c">
          {p.copied ? <CheckIcon className="h-4 w-4 text-success" data-id="element-229" data-mp="knafpo6jwpsy" /> : <CopyIcon className="h-4 w-4" data-id="element-230" data-mp="fq1vzj5r8dpl" />}
          {p.copied ? 'Copied' : 'Copy'}
        </button>
      </header>

      <div className="border-b border-line" data-id="element-231" data-mp="3o6wtej3s95wa">
        <button type="button" onClick={() => p.setCustomizing(!p.customizing)} aria-expanded={p.customizing} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-colors duration-150 ease-out hover:bg-canvas focus:outline-none focus-visible:bg-canvas" data-id="element-232" data-mp="3k4i9dvodo03f">
          <span className="inline-flex items-center gap-2 text-sm font-bold text-ink" data-id="element-233" data-mp="35ekfcz3rog6b">
            <SlidersHorizontalIcon className="h-4 w-4 text-cyan-ink" data-id="element-234" data-mp="30h2dfso6317t" /> Customize the brackets
          </span>
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-muted" data-id="element-235" data-mp="14ccuyxdybk4o">
            {p.filledCount} of {p.fields.length}
            <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${p.customizing ? 'rotate-180' : ''}`} data-id="element-236" data-mp="19aebpbnwn7cz" />
          </span>
        </button>
        {p.customizing && <div className="grid gap-3 bg-canvas px-5 pb-5 pt-2 sm:grid-cols-2" data-id="element-237" data-mp="1j4dca68byj2t">
            {p.fields.map(field => <label key={field} className="flex flex-col gap-1" data-id="element-238" data-mp="qdljzjudyekr">
                <span className="text-[11px] font-semibold text-muted" data-id="element-239" data-mp="qe4zyv0h6sw5">{field}</span>
                <input type="text" value={p.values[field] ?? ''} onChange={e => p.setValue(field, e.target.value)} className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-150 ease-out focus:border-cyan focus:ring-2 focus:ring-cyan/25" data-id="element-240" data-mp="vbwvdz0bwx53" />
              </label>)}
          </div>}
      </div>

      <p className="whitespace-pre-line px-5 py-5 text-[13.5px] leading-relaxed text-ink/85" data-id="element-241" data-mp="3r9nlii7ns0s6">
        {segments.map((s, i) => {
        if (s.type === 'text') return <React.Fragment key={i} data-id="element-242" data-mp="3mgugruc6wsg3">{s.value}</React.Fragment>;
        const val = p.values[s.value]?.trim();
        return val ? <mark key={i} className="rounded bg-cyan-soft px-1 font-semibold text-cyan-ink" data-id="element-243" data-mp="3cl9m6h9gaugd">
              {val}
            </mark> : <mark key={i} className="rounded bg-gold-soft px-1 font-semibold text-gold-ink" data-id="element-244" data-mp="37o11oa0tyeby">
              [{s.value}]
            </mark>;
      })}
      </p>

      <footer className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-4" data-id="element-245" data-mp="2ymjop0ipysrh">
        <span className="mr-1 text-xs font-semibold text-muted" data-id="element-246" data-mp="11rx1t4c49qjh">Open in:</span>
        {aiTargets.map(t => <button key={t.name} type="button" onClick={() => p.openIn(t.url(p.finalText))} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-bold text-ink transition-colors duration-150 ease-out hover:border-cyan hover:text-cyan-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan/40" data-id="element-247" data-mp="wv6kkdoksowh">
            {t.name} <ExternalLinkIcon className="h-3 w-3" data-id="element-248" data-mp="3louxyekq8iso" />
          </button>)}
      </footer>
    </article>;
}